import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Heart, Eye, X, BadgeCheck, Trash2, Pencil, Check, XCircle, Flag } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const VIDEO_REPORT_REASONS = [
  'Inappropriate content',
  'Misleading or fake claims',
  'Spam or repetitive',
  'Harassment or hate speech',
  'Copyright violation',
  'Other',
];

export default function VideoPlayerModal({ video, open, onOpenChange, onAuthRequired, onVideoDeleted, onVideoUpdated }) {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [liked, setLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(video?.likes_count || 0);
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [reportDialog, setReportDialog] = useState(false);
  const [reportReason, setReportReason] = useState('');
  const [reportDetails, setReportDetails] = useState('');
  const [reporting, setReporting] = useState(false);

  const isOwner = isAuthenticated && user && video && user.user_id === video.user_id;

  useEffect(() => {
    if (!video || !open) return;
    setLikesCount(video.likes_count || 0);
    setEditing(false);
    setConfirmDelete(false);
    setReportDialog(false);
    setReportReason('');
    setReportDetails('');
    if (isAuthenticated) {
      axios.get(`${API}/videos/${video.video_id}/like/check`)
        .then(r => setLiked(r.data.liked))
        .catch(() => {});
    }
  }, [video, open, isAuthenticated]);

  const handleReportVideo = async () => {
    if (!reportReason) { toast.error('Please select a reason'); return; }
    setReporting(true);
    try {
      const res = await axios.post(`${API}/videos/${video.video_id}/report`, {
        reason: reportReason,
        details: reportDetails,
      });
      toast.success(res.data.message || 'Report submitted. We\'ll review it shortly.');
      setReportDialog(false);
      setReportReason('');
      setReportDetails('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit report');
    } finally {
      setReporting(false);
    }
  };

  const handleLike = async () => {
    if (!isAuthenticated) { onAuthRequired?.(); return; }
    try {
      const res = await axios.post(`${API}/videos/${video.video_id}/like`, {});
      setLiked(res.data.liked);
      setLikesCount(prev => prev + (res.data.liked ? 1 : -1));
    } catch { toast.error('Failed to like'); }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/videos/${video.video_id}`);
      toast.success('Video deleted');
      onOpenChange(false);
      onVideoDeleted?.(video.video_id);
    } catch {
      toast.error('Failed to delete video');
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  const startEditing = () => {
    setEditTitle(video.title || '');
    setEditDescription(video.description || '');
    setEditing(true);
  };

  const handleSaveEdit = async () => {
    if (!editTitle.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      await axios.put(`${API}/videos/${video.video_id}`, {
        title: editTitle.trim(),
        description: editDescription.trim(),
      });
      toast.success('Video updated');
      setEditing(false);
      onVideoUpdated?.(video.video_id, { title: editTitle.trim(), description: editDescription.trim() });
    } catch {
      toast.error('Failed to update video');
    } finally {
      setSaving(false);
    }
  };

  if (!video) return null;

  const author = video.author || {};
  const displayTitle = editing ? editTitle : video.title;
  const displayDescription = editing ? editDescription : video.description;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl w-[95vw] p-0 rounded-3xl border-cream-100 bg-[#2C2A29] overflow-hidden" data-testid="video-player-modal">
        <button onClick={() => onOpenChange(false)}
          className="absolute top-3 right-3 z-50 bg-black/40 backdrop-blur-sm text-white rounded-full p-1.5 hover:bg-black/60 transition-colors"
          data-testid="video-modal-close">
          <X size={18} />
        </button>

        <div className="relative w-full" style={{ maxHeight: '70vh' }}>
          <video
            src={video.video_url}
            controls
            autoPlay
            className="w-full object-contain bg-black"
            style={{ maxHeight: '70vh' }}
            data-testid="video-player"
          />
        </div>

        <div className="p-5 bg-white rounded-b-3xl">
          <div className="flex items-start justify-between gap-4 mb-3">
            <div className="min-w-0 flex-1">
              <span className="inline-block bg-[#FFF5F5] text-[#D48585] px-2.5 py-0.5 rounded-full text-[11px] font-body font-semibold mb-2" data-testid="video-category-badge">
                {video.category}
              </span>
              {editing ? (
                <input
                  type="text"
                  value={editTitle}
                  onChange={e => setEditTitle(e.target.value)}
                  className="w-full font-heading text-xl text-[#2C2A29] leading-tight border border-cream-100 rounded-lg px-3 py-1.5 focus:outline-none focus:border-[#F4C2C2]"
                  data-testid="video-edit-title"
                  placeholder="Video title"
                />
              ) : (
                <h3 className="font-heading text-xl text-[#2C2A29] leading-tight" data-testid="video-modal-title">{video.title}</h3>
              )}
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              {isOwner && !editing && (
                <>
                  <button onClick={startEditing} className="text-[#A89F91] hover:text-[#C5A880] transition-colors" data-testid="video-edit-btn" title="Edit caption">
                    <Pencil size={18} />
                  </button>
                  {confirmDelete ? (
                    <div className="flex items-center gap-1">
                      <button onClick={handleDelete} disabled={deleting}
                        className="text-red-500 hover:text-red-600 transition-colors disabled:opacity-50" data-testid="video-confirm-delete-btn" title="Confirm delete">
                        <Check size={18} />
                      </button>
                      <button onClick={() => setConfirmDelete(false)}
                        className="text-[#A89F91] hover:text-[#78716C] transition-colors" data-testid="video-cancel-delete-btn" title="Cancel">
                        <XCircle size={18} />
                      </button>
                    </div>
                  ) : (
                    <button onClick={() => setConfirmDelete(true)} className="text-[#A89F91] hover:text-red-500 transition-colors" data-testid="video-delete-btn" title="Delete video">
                      <Trash2 size={18} />
                    </button>
                  )}
                </>
              )}
              {editing && (
                <div className="flex items-center gap-1">
                  <button onClick={handleSaveEdit} disabled={saving}
                    className="text-green-600 hover:text-green-700 transition-colors disabled:opacity-50" data-testid="video-save-edit-btn" title="Save">
                    <Check size={18} />
                  </button>
                  <button onClick={() => setEditing(false)}
                    className="text-[#A89F91] hover:text-[#78716C] transition-colors" data-testid="video-cancel-edit-btn" title="Cancel">
                    <XCircle size={18} />
                  </button>
                </div>
              )}
              <button onClick={handleLike} className="flex items-center gap-1.5 group" data-testid="video-like-btn">
                <Heart size={20} className={`transition-colors ${liked ? 'text-[#E05A5A] fill-[#E05A5A]' : 'text-[#A89F91] group-hover:text-[#E05A5A]'}`} />
                <span className="text-sm font-body font-medium text-[#78716C]">{likesCount}</span>
              </button>
              <div className="flex items-center gap-1.5">
                <Eye size={16} className="text-[#A89F91]" />
                <span className="text-sm font-body text-[#78716C]">{video.views_count || 0}</span>
              </div>
              {/* Non-owners can report; owners can't report their own video. */}
              {!isOwner && (
                <button
                  onClick={() => {
                    if (!isAuthenticated) { onAuthRequired?.(); return; }
                    setReportDialog(true);
                  }}
                  className="ml-auto text-[#A89F91] hover:text-red-600 transition-colors p-1.5 rounded-full hover:bg-red-50"
                  data-testid="video-report-btn"
                  title="Report this video"
                >
                  <Flag size={16} />
                </button>
              )}
            </div>
          </div>

          {editing ? (
            <textarea
              value={editDescription}
              onChange={e => setEditDescription(e.target.value)}
              className="w-full font-body text-sm text-[#78716C] mb-4 leading-relaxed border border-cream-100 rounded-lg px-3 py-2 focus:outline-none focus:border-[#F4C2C2] resize-none"
              rows={3}
              data-testid="video-edit-description"
              placeholder="Video description (optional)"
            />
          ) : displayDescription ? (
            <p className="font-body text-sm text-[#78716C] mb-4 leading-relaxed" data-testid="video-modal-description">{displayDescription}</p>
          ) : null}

          <div className="flex items-center gap-3 pt-3 border-t border-cream-100/50">
            <button
              onClick={() => { onOpenChange(false); navigate(`/profile/${author.user_id}`); }}
              className="flex items-center gap-3 hover:opacity-80 transition-opacity"
              data-testid="video-modal-author">
              {author.picture ? (
                <img src={author.picture} alt={author.name} className="w-9 h-9 rounded-full object-cover border-2 border-blush-200" />
              ) : (
                <div className="w-9 h-9 rounded-full bg-blush-200 flex items-center justify-center text-sm font-bold text-[#2C2A29]">
                  {author.name?.[0]}
                </div>
              )}
              <div>
                <p className="font-body text-sm font-medium text-[#2C2A29] flex items-center gap-1">
                  {author.name}
                  <BadgeCheck size={14} className="text-blue-500 fill-blue-50" />
                  {author.is_demo && (
                    <span className="text-[10px] font-body font-semibold text-amber-600 bg-amber-100 px-1.5 py-0.5 rounded-full ml-1" data-testid="video-modal-demo-badge">
                      Demo
                    </span>
                  )}
                </p>
                {author.is_demo ? (
                  <p className="text-xs font-body text-amber-600 font-medium">Demo influencer</p>
                ) : author.follower_range && (
                  <p className="text-xs font-body text-[#C5A880]">{author.follower_range}</p>
                )}
              </div>
            </button>
          </div>

          <p className="text-xs font-body text-[#A89F91] mt-3">
            {new Date(video.created_at).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' })}
          </p>
        </div>
      </DialogContent>

      {/* Report Video Dialog */}
      <Dialog open={reportDialog} onOpenChange={setReportDialog}>
        <DialogContent className="rounded-3xl border-cream-100 bg-white max-w-md mx-4" data-testid="report-video-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl text-[#2C2A29]">Report this video</DialogTitle>
            <DialogDescription className="font-body text-[#78716C]">
              Tell us what's wrong. Admin will review and decide whether to take it down.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="block text-xs font-body font-medium text-[#78716C] mb-1.5">Reason *</label>
              <div className="grid gap-1.5">
                {VIDEO_REPORT_REASONS.map(reason => (
                  <label
                    key={reason}
                    className={`flex items-center gap-2 cursor-pointer rounded-xl border px-3 py-2 text-sm font-body transition-all ${
                      reportReason === reason
                        ? 'border-[#F4C2C2] bg-blush-50 text-[#2C2A29]'
                        : 'border-cream-100 bg-cream-50 text-[#78716C] hover:bg-cream-100'
                    }`}
                    data-testid={`video-report-reason-${reason.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  >
                    <input
                      type="radio"
                      name="video-report-reason"
                      value={reason}
                      checked={reportReason === reason}
                      onChange={e => setReportReason(e.target.value)}
                      className="accent-[#F4C2C2]"
                    />
                    {reason}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="block text-xs font-body font-medium text-[#78716C] mb-1.5">Additional details (optional)</label>
              <textarea
                value={reportDetails}
                onChange={e => setReportDetails(e.target.value)}
                placeholder="Anything else admin should know?"
                rows={3}
                className="w-full bg-cream-50 border border-cream-100 rounded-xl px-3 py-2.5 font-body text-sm text-[#2C2A29] focus:outline-none focus:border-[#F4C2C2] resize-none"
                data-testid="video-report-details-input"
                maxLength={500}
              />
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => { setReportDialog(false); setReportReason(''); setReportDetails(''); }}
                className="flex-1 bg-cream-100 text-[#78716C] rounded-full px-4 py-2.5 font-body text-sm font-medium hover:bg-cream-200 transition-all"
                data-testid="video-report-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={handleReportVideo}
                disabled={reporting || !reportReason}
                className="flex-1 bg-red-500 text-white rounded-full px-4 py-2.5 font-body text-sm font-semibold hover:bg-red-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="video-report-submit-btn"
              >
                {reporting ? 'Submitting…' : 'Submit Report'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
